/**
 * AI Insights Panel — the single AI surface for a contact (gated crm_ai by
 * the caller): summary, score, suggested actions, plus the raw AI-gathered
 * insights with staff correction affordances. Corrections change live AI
 * behavior — the conversation prompt reads these insights.
 */

import { useEffect, useState, useCallback } from 'react'
import {
  Brain, RefreshCw, Target, TrendingUp, TrendingDown, Minus,
  AlertCircle, CheckSquare, MessageCircle, Phone, X,
} from 'lucide-react'
import { useCRM } from '@/hooks/useCRM'
import { usePermission } from '@/hooks/usePermission'
import { Input, Skeleton, useToast } from '@/components/ui'
import type {
  AiInsights, AiSummary, ContactScore, CrmContactDetail, SuggestedAction,
} from '@/types/crm'

interface AiInsightsPanelProps {
  contact: CrmContactDetail
}

const ACTION_ICONS: Record<string, React.ElementType> = {
  follow_up: Phone,
  create_task: CheckSquare,
  send_message: MessageCircle,
  proactive_outreach: Target,
}

const PRIORITY_COLORS: Record<string, string> = {
  urgent: 'text-destructive bg-destructive/10',
  high: 'text-warning-strong bg-warning/10',
  normal: 'text-info bg-info/10',
  low: 'text-muted-foreground bg-muted',
}

const SENTIMENT_CONFIG: Record<string, { icon: React.ElementType; label: string; color: string }> = {
  positive: { icon: TrendingUp, label: 'Positive', color: 'text-success' },
  neutral: { icon: Minus, label: 'Neutral', color: 'text-muted-foreground' },
  negative: { icon: TrendingDown, label: 'Negative', color: 'text-destructive' },
}

const TREND_CONFIG: Record<string, string> = {
  improving: 'Improving',
  stable: 'Stable',
  declining: 'Declining',
}

export function AiInsightsPanel({ contact }: AiInsightsPanelProps) {
  const contactId = contact.id
  const {
    fetchAiSummary, fetchSuggestedActions, fetchContactScore, updateContactInsights,
  } = useCRM()
  const { addToast } = useToast()
  const canWrite = usePermission('crm:write')

  const [summary, setSummary] = useState<AiSummary | null>(null)
  const [actions, setActions] = useState<SuggestedAction[]>([])
  const [score, setScore] = useState<ContactScore | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)

  const loadAll = useCallback(async () => {
    setIsLoading(true)
    const [summaryResult, actionsResult, scoreResult] = await Promise.all([
      fetchAiSummary(contactId),
      fetchSuggestedActions(contactId),
      fetchContactScore(contactId),
    ])
    setSummary(summaryResult)
    setActions(actionsResult)
    setScore(scoreResult)
    setIsLoading(false)
  }, [contactId, fetchAiSummary, fetchSuggestedActions, fetchContactScore])

  useEffect(() => {
    loadAll()
  }, [loadAll])

  const saveInsights = useCallback(async (patch: AiInsights) => {
    setIsSaving(true)
    try {
      const updated = await updateContactInsights(contactId, patch)
      if (updated) {
        addToast({ type: 'success', title: 'Insights updated' })
      } else {
        addToast({
          type: 'error',
          title: 'Failed to update insights',
          description: 'Check your connection and try again.',
        })
      }
    } finally {
      setIsSaving(false)
    }
  }, [contactId, updateContactInsights, addToast])

  const insights = contact.aiInsights
  const hasRaw = !!insights && (
    (insights.dietary?.length ?? 0) > 0 ||
    (insights.declined?.length ?? 0) > 0 ||
    (insights.events?.length ?? 0) > 0 ||
    (insights.observations?.length ?? 0) > 0 ||
    Object.keys(insights.preferences ?? {}).length > 0
  )

  if (isLoading) {
    return (
      <div className="space-y-4">
        <SectionHeader title="AI Insights" icon={Brain} />
        <div className="space-y-2 px-1">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-3/4" />
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Summary */}
      {summary && (
        <div>
          <div className="flex items-center justify-between">
            <SectionHeader title="AI Summary" icon={Brain} />
            <button onClick={loadAll} className="p-1 rounded-sm hover:bg-muted" title="Refresh">
              <RefreshCw className="w-3 h-3 text-muted-foreground" />
            </button>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed mt-1 px-1">
            {summary.summary}
          </p>
        </div>
      )}

      {/* Score */}
      {score && (
        <div>
          <SectionHeader title="Contact Score" icon={Target} />
          <div className="mt-1.5 px-1">
            {/* threshold-colored; Progress primitive has no color variants */}
            <div className="flex items-center gap-2 mb-2">
              <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    score.totalScore >= 70 ? 'bg-success' :
                    score.totalScore >= 40 ? 'bg-warning' : 'bg-destructive'
                  }`}
                  style={{ width: `${score.totalScore}%` }}
                />
              </div>
              <span className="text-sm font-bold text-foreground w-8 text-right">
                {score.totalScore}
              </span>
            </div>

            {/* Components */}
            <div className="grid grid-cols-2 gap-1.5">
              {score.components.map((c) => (
                <div key={c.name} className="text-[10px]">
                  <span className="text-muted-foreground">{c.name}:</span>{' '}
                  <span className="font-medium text-muted-foreground">{c.score}</span>
                </div>
              ))}
            </div>

            {/* Sentiment */}
            {score.sentiment && (
              <div className="flex items-center gap-2 mt-2 text-xs">
                {(() => {
                  const cfg = SENTIMENT_CONFIG[score.sentiment] || SENTIMENT_CONFIG.neutral
                  const SentimentIcon = cfg.icon
                  return (
                    <>
                      <SentimentIcon className={`w-3.5 h-3.5 ${cfg.color}`} />
                      <span className={cfg.color}>{cfg.label}</span>
                      {score.sentimentTrend && (
                        <span className="text-muted-foreground">
                          {TREND_CONFIG[score.sentimentTrend] || score.sentimentTrend}
                        </span>
                      )}
                    </>
                  )
                })()}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Suggested Actions */}
      {actions.length > 0 && (
        <div>
          <SectionHeader title="Suggested Actions" icon={AlertCircle} />
          <div className="mt-1.5 space-y-1.5">
            {actions.map((action) => {
              const ActionIcon = ACTION_ICONS[action.actionType] || Target
              const priorityClass = PRIORITY_COLORS[action.priority] || PRIORITY_COLORS.normal
              return (
                <div
                  key={action.id}
                  className={`p-2 rounded-lg text-xs ${priorityClass}`}
                >
                  <div className="flex items-start gap-1.5">
                    <ActionIcon className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium">{action.title}</p>
                      <p className="opacity-75 mt-0.5">{action.reason}</p>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Raw AI-gathered insights — read by the conversation prompt */}
      {(hasRaw || canWrite) && (
        <div>
          <SectionHeader title="Customer Insights" icon={Brain} />
          <div className="mt-1.5 space-y-3 px-1">
            {insights?.preferences && Object.keys(insights.preferences).length > 0 && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Preferences</p>
                <div className="space-y-0.5">
                  {Object.entries(insights.preferences).map(([key, value]) => (
                    <div key={key} className="flex items-center gap-1 text-xs text-muted-foreground">
                      <span className="flex-1 min-w-0 truncate">
                        <span className="capitalize">{key.replace(/_/g, ' ')}</span>: {value}
                      </span>
                      {canWrite && (
                        <RemoveButton
                          label={`Remove preference ${key}`}
                          disabled={isSaving}
                          onClick={() => {
                            const { [key]: _removed, ...rest } = insights.preferences ?? {}
                            saveInsights({ preferences: rest })
                          }}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <ChipListEditor
              label="Dietary"
              items={insights?.dietary ?? []}
              canWrite={canWrite}
              disabled={isSaving}
              addPlaceholder="Add dietary entry"
              removeLabel={(item) => `Remove dietary ${item}`}
              onSave={(next) => saveInsights({ dietary: next })}
            />

            <ChipListEditor
              label="Declined"
              hint="Themes the customer pushed back on — the AI won't suggest these unprompted"
              items={insights?.declined ?? []}
              canWrite={canWrite}
              disabled={isSaving}
              addPlaceholder="Add declined theme"
              removeLabel={(item) => `Remove declined ${item}`}
              onSave={(next) => saveInsights({ declined: next })}
            />

            {insights?.events && insights.events.length > 0 && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Events</p>
                <div className="space-y-1">
                  {insights.events.map((event, i) => (
                    <div key={i} className="flex items-center gap-1 text-xs text-muted-foreground">
                      <span className="flex-1 min-w-0 truncate">
                        <span className="capitalize">{event.type}</span>
                        {event.date && <span> — {event.date}</span>}
                        {event.notes && <span> ({event.notes})</span>}
                      </span>
                      {canWrite && (
                        <RemoveButton
                          label={`Remove event ${event.type}`}
                          disabled={isSaving}
                          onClick={() => saveInsights({
                            events: (insights.events ?? []).filter((_, j) => j !== i),
                          })}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {insights?.observations && insights.observations.length > 0 && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Observations</p>
                <div className="space-y-1">
                  {insights.observations.map((obs, i) => (
                    <div key={i} className="flex items-center gap-1 text-xs text-muted-foreground">
                      <span className="flex-1 min-w-0">
                        {obs.note}
                        {obs.date && <span className="opacity-75"> — {obs.date}</span>}
                      </span>
                      {canWrite && (
                        <RemoveButton
                          label={`Remove observation ${i + 1}`}
                          disabled={isSaving}
                          onClick={() => saveInsights({
                            observations: (insights.observations ?? []).filter((_, j) => j !== i),
                          })}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function SectionHeader({ title, icon: Icon }: { title: string; icon: React.ElementType }) {
  return (
    <div className="flex items-center gap-1.5">
      <Icon className="w-3.5 h-3.5 text-muted-foreground" />
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{title}</h3>
    </div>
  )
}

function ChipListEditor({ label, hint, items, canWrite, disabled, addPlaceholder, removeLabel, onSave }: {
  label: string
  hint?: string
  items: string[]
  canWrite: boolean
  disabled?: boolean
  addPlaceholder: string
  removeLabel: (item: string) => string
  onSave: (next: string[]) => void
}) {
  const [draft, setDraft] = useState('')
  if (!canWrite && items.length === 0) return null
  const commitDraft = () => {
    const value = draft.trim().toLowerCase()
    setDraft('')
    if (!value || items.includes(value)) return
    onSave([...items, value])
  }
  return (
    <div>
      <p className="text-xs font-medium text-muted-foreground mb-1">{label}</p>
      {hint && <p className="text-[10px] text-muted-foreground/75 mb-1">{hint}</p>}
      {items.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-1">
          {items.map((item) => (
            <span
              key={item}
              className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-xs rounded-sm bg-muted text-muted-foreground"
            >
              {item}
              {canWrite && (
                <RemoveButton
                  label={removeLabel(item)}
                  disabled={disabled}
                  onClick={() => onSave(items.filter((v) => v !== item))}
                />
              )}
            </span>
          ))}
        </div>
      )}
      {canWrite && (
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              commitDraft()
            }
          }}
          placeholder={addPlaceholder}
          aria-label={addPlaceholder}
          disabled={disabled}
        />
      )}
    </div>
  )
}

function RemoveButton({ label, onClick, disabled }: { label: string; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      className="p-0.5 rounded-sm hover:bg-muted-foreground/10 text-muted-foreground disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <X className="w-3 h-3" />
    </button>
  )
}
